import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { SolarProductType } from "../../model/solar-product/solar-product-type";
import solarProductService from "../../httpService/SolarProductService";
import { FileFromObjectUrl } from "../../model/common/file-from-object-url";
import { RootState } from "../store";

export type SolarProductDetailsState = {
    editingProductId?: string
    currentStep: number
    error?: string
    firstStep: FirstStepOfSolarProductDetailsState
    secondStep: SecondStepOfSolarProductDetailsState
    thirdStep: ThirdStepOfSolarProductDetailsState
}

export type FirstStepOfSolarProductDetailsState = {
    productName: string
    productCapacity: number
    price: string
    activeDuration?: string
    packageDescription?: string
    batteryType?: string
    categoryIds :  string[]
}

export type SecondStepOfSolarProductDetailsState = {
    inverterType?: string
    warranty?: string
    batteryVoltage?: string
    type: SolarProductType
    quantityAvailableForSale?: string
}

export type ThirdStepOfSolarProductDetailsState = {
    costForBungalow: string
    costPerFloor: string
    existingImages?: FileFromObjectUrl[]
    images: FileFromObjectUrl[]
}

const addNewProduct = createAsyncThunk<void, void, { state: RootState }>(
    'solarProductDetails/addNewProduct',
    async ( _, { getState }) => {
        const productInfo = getState().solarProductDetails;

        await solarProductService.add(productInfo);
    },
)

const editProduct = createAsyncThunk<void, void, { state: RootState }>(
    'solarProductDetails/editProduct',
    async ( _, { getState }) => {
        const productInfo = getState().solarProductDetails;

        await solarProductService.edit(productInfo);
    },
)

const initialState: SolarProductDetailsState = {
    currentStep: 1,
    firstStep: {
        productName: '',
        productCapacity: 0,
        price: '',
        activeDuration: '',
        packageDescription: '',
        batteryType: '',
        categoryIds: [],
    },
    secondStep: {
        inverterType: '',
        warranty: '',
        batteryVoltage: '',
        type: SolarProductType.STORAGE_ONLY,
        quantityAvailableForSale: ''
    },
    thirdStep: {
        costForBungalow: '',
        costPerFloor: '',
        images: [],
    }
}

export const solarProductDetailsSlice = createSlice({
    name: 'solarProductDetails',
    initialState: initialState,
    reducers: {
        setupSolarProductDetails: (state, action : PayloadAction<SolarProductDetailsState | undefined>) => {
            state.error = initialState.error;
            state.editingProductId = initialState.editingProductId;
            state.currentStep = initialState.currentStep;
            if (action.payload) {
                state.editingProductId = action.payload.editingProductId;
                state.firstStep = {
                    ...action.payload.firstStep
                };
                state.secondStep = {
                    ...action.payload.secondStep
                };
                state.thirdStep = {
                    ...action.payload.thirdStep
                };
            } else {
                state.firstStep = {
                    ...initialState.firstStep
                };
                state.secondStep = {
                    ...initialState.secondStep
                };
                state.thirdStep = {
                    ...initialState.thirdStep,
                    existingImages: [],
                };
            }
        },
        moveToNextStep: (state) => {
            if (state.currentStep < 3) {
                state.currentStep += 1;
            }
        },
        moveToPreviousStep: (state) => {
            if (state.currentStep > 1) {
                state.currentStep -= 1;
            }
        },
        submitFirstStep: (state, action) => {
            state.firstStep = action.payload;
            state.currentStep = 2;
        },
        submitSecondStep: (state, action) => {
            state.secondStep = action.payload;
            state.currentStep = 3;
        },
        submitThirdStep: (state, action) => {
            state.thirdStep = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(addNewProduct.fulfilled, (state) => {
            state.currentStep = initialState.currentStep;
            state.error = undefined;
            state.firstStep = initialState.firstStep;
            state.secondStep = initialState.secondStep;
            state.thirdStep = initialState.thirdStep;
        });
        builder.addCase(addNewProduct.rejected, (state, action) => {
            state.error = action.error.message;
        });
        builder.addCase(editProduct.fulfilled, (state) => {
            state.currentStep = initialState.currentStep;
            state.error = undefined;
            state.firstStep = initialState.firstStep;
            state.secondStep = initialState.secondStep;
            state.thirdStep = initialState.thirdStep;
        });
        builder.addCase(editProduct.rejected, (state, action) => {
            state.error = action.error.message;
        });
    }
});

export const { moveToNextStep, moveToPreviousStep, submitFirstStep, submitSecondStep, submitThirdStep, setupSolarProductDetails } = solarProductDetailsSlice.actions;

export { addNewProduct, editProduct };

export default solarProductDetailsSlice.reducer;